import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowLeft, FiMoon, FiSun, FiMessageCircle } from "react-icons/fi";

const countryCodes = [
  { code: "+91", label: "🇮🇳 India" },
  { code: "+1", label: "🇨🇦 Canada" },
  { code: "+44", label: "🇬🇧 UK" },
  { code: "+971", label: "🇦🇪 UAE" },
  { code: "+61", label: "🇦🇺 Australia" },
];

const chatbotData = {
  Payments: [
    { q: "What payment methods are accepted?", a: "We accept UPI, cards, and cash on delivery." },
    { q: "How do I apply a promo code?", a: "Enter it on the checkout page before paying." },
    { q: "Can I tip the delivery partner?", a: "Yes, after delivery in the order summary screen." },
  ],
  Orders: [
    { q: "How do I cancel an order?", a: "Go to 'Orders' > tap 'Cancel' before it's prepared." },
    { q: "Can I reorder the same meal?", a: "Yes, tap 'Reorder' from your past orders." },
    { q: "Can I schedule an order?", a: "Yes! Choose 'Schedule for later' at checkout." },
  ],
  Delivery: [
    { q: "Where’s my order?", a: "Track it live under the 'Orders' tab." },
    { q: "Is contactless delivery available?", a: "Yes, choose 'Leave at door' during checkout." },
    { q: "What if my order is late?", a: "Delays may happen due to traffic/weather." },
  ],
  Account: [
    { q: "Can I change my phone number?", a: "Yes, from your profile settings." },
    { q: "Can I update my delivery address?", a: "Yes, during checkout or in profile settings." },
    { q: "Do I need an account to order?", a: "No, you can use Guest Mode to order without signing up." },
  ],
};

const PhoneLogin = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [selectedCode, setSelectedCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState(() => {
    const saved = sessionStorage.getItem("chatHistory");
    return saved ? JSON.parse(saved) : [{ type: "bot", text: "Hi! How can I assist you today?" }];
  });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const otpInputs = useRef([]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  useEffect(() => {
    sessionStorage.setItem("chatHistory", JSON.stringify(chatHistory));
  }, [chatHistory]);

  const handleSendOtp = async () => {
    const fullPhone = selectedCode + phone;
    if (!/^\+[1-9]\d{9,14}$/.test(fullPhone)) {
      setError("Enter a valid phone number including country code.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const result = await signInWithPhoneNumber(auth, fullPhone);
      setConfirmationResult(result);
      setTimer(60);
    } catch (err) {
      setError("Failed to send OTP: " + (err.message || "Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeOtp = (val, index) => {
    if (/^\d?$/.test(val)) {
      const updated = [...otp];
      updated[index] = val;
      setOtp(updated);
      if (val && index < 5) otpInputs.current[index + 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.some((d) => d === "")) {
      setError("Enter all 6 digits.");
      return;
    }
    setError("");
    setIsLoading(true);
    const code = otp.join("");
    try {
      const result = await confirmationResult.confirm(code);
      const user = result.user;
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("profile", JSON.stringify({
        name: user.displayName || "",
        phone: user.phoneNumber,
        email: "",
        avatar: "",
        address: "",
        upi: "",
      }));
      navigate("/landing");
    } catch (err) {
      setError("Incorrect OTP. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setChatHistory((prev) => [...prev, { type: "user", text: topic }]);
  };

  const handleQuestionSelect = (q, a) => {
    setChatHistory((prev) => [...prev, { type: "user", text: q }]);
    setIsTyping(true);
    setTimeout(() => {
      setChatHistory((prev) => [...prev, { type: "bot", text: a }]);
      setIsTyping(false);
    }, 1000);
  };

  const resetChat = () => {
    setChatHistory([{ type: "bot", text: "Hi! How can I assist you today?" }]);
    setSelectedTopic(null);
    sessionStorage.removeItem("chatHistory");
  };

  return (
    <div className={`min-h-screen flex flex-col font-montserrat ${darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
          .font-montserrat { font-family: 'Montserrat', sans-serif; }
          .typing-dot { font-size: 24px; animation: bounce 1s infinite; }
          .animate-delay-150 { animation-delay: 0.15s; }
          .animate-delay-300 { animation-delay: 0.3s; }
          @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
          .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #00b86b; border-radius: 6px; }
          .dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #00ff8a; }
          button:focus, input:focus, select:focus { outline: none; box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.3); }
          .input-transition { transition: all 0.3s ease; }
          .hover-scale { transition: transform 0.2s ease; }
          .hover-scale:hover { transform: scale(1.05); }
          @media (max-width: 400px) {
            .max-w-lg { max-width: 100%; padding-left: 1rem; padding-right: 1rem; }
            h1 { font-size: 1.5rem; }
            p, button, input, select { font-size: 0.875rem; }
          }
        `}
      </style>

      {/* Header */}
      <motion.header
        initial={{ y: -50 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`sticky top-0 z-20 py-4 px-6 flex justify-between items-center ${darkMode ? "bg-gradient-to-r from-gray-800 to-gray-900" : "bg-white"} shadow-sm`}
      >
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-xl hover-scale"><FiArrowLeft /></button>
          <motion.img src="/zoomo-logo.png" alt="Zoomo Logo" className="w-10 h-10" whileHover={{ scale: 1.1 }} />
          <h1 className="text-xl font-bold">ZOOMO <span className="text-green-500">EATS</span></h1>
        </div>
        <motion.button
          onClick={toggleDarkMode}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 rounded-full bg-green-500 text-white hover-scale"
        >
          {darkMode ? <FiSun /> : <FiMoon />}
        </motion.button>
      </motion.header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center py-8 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`w-full max-w-lg p-8 rounded-3xl ${darkMode ? "bg-gray-800/80 border-gray-700" : "bg-white border-gray-200"} border shadow-2xl backdrop-blur-sm`}
        >
          <div className="text-center mb-6">
            <motion.img src="/zoomo-mascot.png" className="w-16 mx-auto mb-4" whileHover={{ y: -5 }} alt="Mascot" />
            <h1 className="text-2xl font-bold">Phone Number Login</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">Verify your phone to continue with Zoomo Eats</p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-4 p-3 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 rounded-lg text-sm"
            >
              {error}
            </motion.div>
          )}

          {!confirmationResult ? (
            <>
              <div className="flex gap-3 mb-6">
                <div className="relative w-1/3">
                  <select
                    value={selectedCode}
                    onChange={(e) => setSelectedCode(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border input-transition ${darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"} focus:outline-none focus:ring-2 focus:ring-green-500 text-sm`}
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label} {c.code}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative flex-1">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border input-transition ${darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"} focus:outline-none focus:ring-2 focus:ring-green-500 peer`}
                    required
                  />
                  <label
                    className={`absolute left-4 top-3 text-gray-500 transition-all duration-200 peer-focus:-top-6 peer-focus:text-sm peer-focus:text-green-500 peer-valid:-top-6 peer-valid:text-sm ${
                      phone ? "-top-6 text-sm" : ""
                    }`}
                  >
                    Phone Number
                  </label>
                </div>
              </div>
              <motion.button
                onClick={handleSendOtp}
                disabled={timer > 0 || isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-3 rounded-lg font-bold input-transition ${
                  timer > 0 || isLoading ? "bg-gray-500 text-white cursor-not-allowed" : "bg-green-500 text-white hover:bg-green-600"
                }`}
              >
                {isLoading ? "Sending OTP..." : timer > 0 ? `Resend OTP in ${timer}s` : "Send OTP"}
              </motion.button>
            </>
          ) : (
            <>
              <div className="flex justify-between gap-3 mb-6">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    type="text"
                    maxLength="1"
                    value={digit}
                    ref={(el) => (otpInputs.current[i] = el)}
                    onChange={(e) => handleChangeOtp(e.target.value, i)}
                    className={`w-12 h-12 text-center px-4 py-2 rounded-lg border input-transition ${
                      darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-green-500 text-lg font-semibold`}
                  />
                ))}
              </div>
              <motion.button
                onClick={handleVerifyOtp}
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-3 rounded-lg bg-green-500 text-white font-bold input-transition ${
                  isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-green-600"
                }`}
              >
                {isLoading ? "Verifying..." : "Verify OTP"}
              </motion.button>
            </>
          )}
        </motion.div>
      </main>

      {/* Chatbot */}
      <motion.button
        onClick={() => setChatOpen(!chatOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-xl z-50 hover-scale"
      >
        <FiMessageCircle size={24} />
      </motion.button>

      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-20 right-6 w-80 max-h-[70vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="bg-green-500 text-white p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <img src="/zoomo-mascot.png" alt="Mascot" className="w-6 h-6 rounded-full" />
                <h2 className="font-bold">Zoomo Bot</h2>
              </div>
              <button onClick={() => setChatOpen(false)} className="text-white hover-scale">✖</button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto h-[50vh] custom-scrollbar">
              {chatHistory.map((entry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: entry.type === "user" ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${entry.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-xl ${
                      entry.type === "user" ? "bg-green-500 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                    }`}
                  >
                    <p>{entry.text}</p>
                    <span className="block text-xs text-gray-400 mt-1">
                      {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-xl flex gap-1">
                    <span className="typing-dot">.</span>
                    <span className="typing-dot animate-delay-150">.</span>
                    <span className="typing-dot animate-delay-300">.</span>
                  </div>
                </div>
              )}
              {!selectedTopic && !isTyping && (
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(chatbotData).map((topic) => (
                    <motion.button
                      key={topic}
                      onClick={() => handleTopicSelect(topic)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`p-2 rounded-lg input-transition ${
                        darkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-green-100 text-gray-900 hover:bg-green-200"
                      } text-sm`}
                    >
                      {topic}
                    </motion.button>
                  ))}
                </div>
              )}
              {selectedTopic && !chatHistory.some((e) => e.type === "bot") && !isTyping && (
                <div className="space-y-2">
                  {chatbotData[selectedTopic].map((item, i) => (
                    <motion.button
                      key={i}
                      onClick={() => handleQuestionSelect(item.q, item.a)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full text-left p-2 rounded-lg input-transition ${
                        darkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                      } text-sm`}
                    >
                      {item.q}
                    </motion.button>
                  ))}
                </div>
              )}
              {!isTyping && selectedTopic && chatHistory.some((e) => e.type === "bot") && (
                <div className="text-center text-sm space-y-2">
                  <button onClick={resetChat} className="text-green-500 hover:underline">
                    Back to Topics
                  </button>
                  <div>
                    Still need help?{" "}
                    <a
                      href="https://wa.me/919070055040"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-500 hover:underline"
                    >
                      Chat on WhatsApp
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`py-4 text-center ${darkMode ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-600"}`}
      >
        <p className="text-sm">
          © {new Date().getFullYear()} <span className="font-semibold text-green-500">Zoomo Eats</span> — Designed by Shiv
        </p>
      </motion.footer>
    </div>
  );
};

export default PhoneLogin;